/**
 * drawer.js
 *
 * One reusable drawer controller: open/close, scrim close, Escape, body
 * scroll lock + restoration, focus trap, focus restoration to trigger,
 * aria-expanded state on the trigger, and mutual exclusion (opening one
 * drawer closes any other that's currently open).
 *
 * Used by BOTH the Cart Drawer (snippets/cart-drawer.liquid, rendered once
 * globally) and the Mobile Menu (snippets/mobile-menu.liquid, rendered
 * from inside sections/header.liquid). Intentionally generic — do not
 * special-case either drawer here; if a future drawer's reference
 * behavior genuinely matches this open/close/scrim/Esc/focus-trap
 * pattern, it can reuse this same controller too.
 *
 * Markup contract (see snippets/cart-drawer.liquid, snippets/mobile-menu.liquid):
 *   [data-drawer-root]     wraps the whole drawer + its scrim
 *   [data-drawer]          the drawer panel itself (gets aria-hidden)
 *   [data-drawer-trigger]  = id of the element that opens this drawer
 *   [data-drawer-scrim]    the backdrop, closes on click
 *   [data-drawer-close]    any element inside the drawer that closes it
 *
 * Theme Editor resilience: a drawer root rendered inside a Shopify section
 * (e.g. the Mobile Menu, nested in the Header section) is replaced
 * wholesale whenever that section reloads — the old root is simply
 * dropped (no reference retained) and a new MvcDrawer instance is created
 * for the new root on the next init pass. A drawer root rendered once at
 * the theme layout level (the Cart Drawer) persists for the drawer's
 * lifetime, but its trigger still lives inside the Header section and CAN
 * be replaced independently — the trigger is therefore never cached as a
 * long-lived element reference, only its id is stored and re-resolved via
 * document.getElementById() on every open/close/rebind call.
 *
 * shopify:section:unload: if Theme Editor removes/re-renders a section
 * whose drawer root is currently open (e.g. the Mobile Menu, embedded in
 * the Header section), that instance's DOM is about to be destroyed —
 * teardown() clears body scroll lock, restores scroll position, removes
 * the document-level keydown listener, and clears activeDrawer, WITHOUT
 * attempting to restore focus to a trigger that is being removed. The
 * persistent global Cart Drawer's own root is never inside a section, so
 * it is unaffected by any section's unload and keeps working — it simply
 * rebinds its trigger again on the following shopify:section:load.
 *
 * inert: closed panels carry the `inert` attribute (in addition to
 * aria-hidden="true") so their buttons/links are removed from sequential
 * keyboard focus entirely while off-screen, rather than merely being
 * translated out of view. open() removes it before moving focus inside;
 * close()/teardown() restore it.
 */
(function () {
  'use strict';

  var registry = new WeakMap();
  var activeDrawer = null;

  function focusableIn(panel) {
    return Array.from(
      panel.querySelectorAll(
        'button:not(:disabled),[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
      )
    ).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function MvcDrawer(root) {
    this.root = root;
    this.panel = root.querySelector('[data-drawer]');
    this.scrim = root.querySelector('[data-drawer-scrim]');
    this.triggerId = this.panel ? this.panel.getAttribute('data-drawer-trigger') : null;
    this.scrollY = 0;
    this.lastFocused = null;

    this._onKeydown = this._onKeydown.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);

    this._bindStatic();
    this.rebindTrigger();
  }

  // Scrim and in-drawer close buttons live inside this drawer's own root.
  // For a root rendered once globally (Cart Drawer) that root is never
  // itself re-rendered by a section reload, so binding once here is
  // enough. For a root rendered inside a section (Mobile Menu), the whole
  // root — including these elements — is recreated on reload, and a
  // fresh MvcDrawer instance runs this again against the new nodes.
  MvcDrawer.prototype._bindStatic = function () {
    if (this.scrim && !this.scrim.dataset.mvcDrawerBound) {
      this.scrim.addEventListener('click', this.close);
      this.scrim.dataset.mvcDrawerBound = 'true';
    }
    var self = this;
    this.root.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
      if (!btn.dataset.mvcDrawerBound) {
        btn.addEventListener('click', self.close);
        btn.dataset.mvcDrawerBound = 'true';
      }
    });
  };

  // Re-resolves the trigger by id and binds it if it isn't already bound.
  // Safe to call repeatedly (idempotent) and safe to call after Theme
  // Editor replaces the trigger element entirely. Also normalizes the
  // trigger's initial aria-expanded state.
  MvcDrawer.prototype.rebindTrigger = function () {
    if (!this.triggerId) return;
    var trigger = document.getElementById(this.triggerId);
    if (!trigger) return;
    if (!trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', 'false');
    }
    if (!trigger.dataset.mvcDrawerBound) {
      trigger.addEventListener('click', this.open);
      trigger.dataset.mvcDrawerBound = 'true';
    }
  };

  MvcDrawer.prototype.currentTrigger = function () {
    return this.triggerId ? document.getElementById(this.triggerId) : null;
  };

  MvcDrawer.prototype.open = function () {
    if (!this.panel || this.panel.classList.contains('on')) return;

    // Mutual exclusion: never leave a different drawer open underneath
    // this one.
    if (activeDrawer && activeDrawer !== this) {
      activeDrawer.close();
    }

    // Must happen before focus is moved inside — otherwise the browser
    // would refuse to focus a still-inert element.
    this.panel.removeAttribute('inert');

    this.lastFocused = document.activeElement;
    this.scrollY = window.scrollY;
    document.body.style.top = '-' + this.scrollY + 'px';
    document.body.classList.add('lock');
    this.panel.classList.add('on');
    if (this.scrim) this.scrim.classList.add('on');
    this.panel.setAttribute('aria-hidden', 'false');
    var trigger = this.currentTrigger();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this._onKeydown);
    activeDrawer = this;
    var self = this;
    window.requestAnimationFrame(function () {
      var f = focusableIn(self.panel);
      if (f.length) f[0].focus();
    });
  };

  MvcDrawer.prototype.close = function () {
    if (!this.panel || !this.panel.classList.contains('on')) return;
    this.panel.classList.remove('on');
    if (this.scrim) this.scrim.classList.remove('on');
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.setAttribute('inert', '');
    document.body.classList.remove('lock');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollY);
    document.removeEventListener('keydown', this._onKeydown);
    if (activeDrawer === this) activeDrawer = null;
    // Resolved fresh (never cached) so focus lands on whichever trigger
    // node currently exists, even if Theme Editor swapped it out while
    // the drawer was open.
    var current = this.currentTrigger();
    if (current) current.setAttribute('aria-expanded', 'false');
    var restore = (current && document.contains(current)) ? current : this.lastFocused;
    if (restore && typeof restore.focus === 'function') restore.focus();
  };

  // Called when this drawer's own section is about to be unloaded by
  // Theme Editor (see the shopify:section:unload handler below). Clears
  // every side effect open() may have left behind — open state, scrim
  // state, body scroll lock, the document-level keydown listener, and
  // activeDrawer — WITHOUT restoring focus, since the trigger itself is
  // being removed along with the rest of the section's DOM.
  MvcDrawer.prototype.teardown = function () {
    if (!this.panel) return;
    var wasOpen = this.panel.classList.contains('on');
    this.panel.classList.remove('on');
    if (this.scrim) this.scrim.classList.remove('on');
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.setAttribute('inert', '');
    if (wasOpen) {
      document.body.classList.remove('lock');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY);
      document.removeEventListener('keydown', this._onKeydown);
    }
    if (activeDrawer === this) activeDrawer = null;
  };

  MvcDrawer.prototype._onKeydown = function (e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    if (e.key !== 'Tab' || !this.panel) return;
    var f = focusableIn(this.panel);
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  function initDrawers() {
    document.querySelectorAll('[data-drawer-root]').forEach(function (root) {
      var instance = registry.get(root);
      if (!instance) {
        instance = new MvcDrawer(root);
        registry.set(root, instance);
      } else {
        instance.rebindTrigger();
      }
    });
  }

  // Any drawer root that lives inside the section being unloaded gets
  // torn down. A drawer root rendered once at the theme layout level
  // (outside every section, like the Cart Drawer) is never matched here
  // and is therefore never affected by a section's unload.
  function handleSectionUnload(e) {
    var sectionId = e && e.detail && e.detail.sectionId;
    if (!sectionId) return;
    var wrapper = document.getElementById('shopify-section-' + sectionId);
    if (!wrapper) return;
    document.querySelectorAll('[data-drawer-root]').forEach(function (root) {
      if (!wrapper.contains(root)) return;
      var instance = registry.get(root);
      if (instance) instance.teardown();
    });
  }

  window.MvcDrawer = MvcDrawer;

  document.addEventListener('DOMContentLoaded', initDrawers);
  document.addEventListener('shopify:section:load', initDrawers);
  document.addEventListener('shopify:section:reorder', initDrawers);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
